import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, hasRole } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { Plus, ShieldCheck, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import MultiRegionPicker from "../components/MultiRegionPicker";

const ROLE_BADGE = {
    super_admin: "bg-red-500/10 text-red-400 border-red-500/20",
    admin: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    management: "bg-violet-500/10 text-violet-500 border-violet-500/20",
    advertiser: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

export default function Users() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const apiLoading = useApiLoading();
    const [agencies, setAgencies] = useState([]);
    const [advertisers, setAdvertisers] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [createForm, setCreateForm] = useState({
        email: "",
        password: "",
        full_name: "",
        role: "admin",
        agency_id: user.role === "admin" ? user.agency_id : "",
        advertiser_id: "",
        allowed_regions: [],
        can_view_actual_cost: false,
        can_manage_branding: false,
        can_connect_integrations: true,
    });

    const load = async () => {
        const { data } = await api.get("/auth/users");
        setUsers(data);
    };
    useEffect(() => {
        load();
        if (user.role === "super_admin") api.get("/agencies").then((r) => setAgencies(r.data));
        if (hasRole(user, "super_admin", "admin")) api.get("/advertisers").then((r) => setAdvertisers(r.data));
    }, [user.role]); // eslint-disable-line

    const pager = usePagination(users, 25);

    const submitCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...createForm };
            if (payload.role !== "advertiser") payload.advertiser_id = null;
            if (payload.role !== "management") payload.allowed_regions = null;
            else if (!payload.allowed_regions?.length) {
                toast.error("Select at least one region (country) for management users");
                return;
            }
            if (user.role === "admin") payload.agency_id = user.agency_id;
            if (!payload.agency_id) delete payload.agency_id;
            await api.post("/auth/users", payload);
            toast.success("User created");
            setCreateOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const togglePerm = async (u, field) => {
        try {
            await api.patch(`/auth/users/${u.id}`, { [field]: !u[field] });
            toast.success("Updated");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };
    const toggleActive = async (u) => {
        try {
            await api.patch(`/auth/users/${u.id}`, { is_active: !u.is_active });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };
    const resetPwd = async (u) => {
        const pwd = window.prompt(`New password for ${u.email}:`);
        if (!pwd) return;
        try {
            await api.patch(`/auth/users/${u.id}`, { password: pwd });
            toast.success("Password reset");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <div data-testid="users-page">
            <PageHeader
                eyebrow="Access control"
                title="Users & permissions"
                subtitle="Create admins, advertiser portal users, and toggle the per-user permissions that gate actual-cost visibility, branding, and integrations."
                actions={
                    <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="user-create-btn">
                        <Plus className="h-4 w-4" /> New user
                    </button>
                }
            />

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">User</th>
                            <th className="overline text-left p-3">Role</th>
                            <th className="overline text-center p-3 whitespace-nowrap">Campaign Spends</th>
                            <th className="overline text-center p-3">Branding</th>
                            <th className="overline text-center p-3">Integrations</th>
                            <th className="overline text-left p-3">Status</th>
                            <th className="overline text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((u) => (
                            <tr key={u.id} className="border-b border-zinc-200 last:border-0">
                                <td className="p-3">
                                    <div className="text-zinc-900">{u.full_name}</div>
                                    <div className="text-xs text-zinc-500">{u.email}</div>
                                </td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${ROLE_BADGE[u.role]}`}>{u.role.replace("_", " ")}</span>
                                </td>
                                {["can_view_actual_cost", "can_manage_branding", "can_connect_integrations"].map((field) => (
                                    <td key={field} className="p-3 text-center">
                                        <button
                                            onClick={() => togglePerm(u, field)}
                                            disabled={u.role === "advertiser" || u.role === "super_admin"}
                                            className={`inline-flex items-center justify-center h-5 w-9 rounded-full transition-colors ${u[field] ? "wl-accent-bg" : "bg-zinc-200"} disabled:opacity-30 disabled:cursor-not-allowed`}
                                            title={u[field] ? "Enabled — click to disable" : "Disabled — click to enable"}
                                            data-testid={`perm-${field}-${u.id}`}
                                        >
                                            <span className={`h-3.5 w-3.5 rounded-full bg-white transition-transform ${u[field] ? "translate-x-2" : "-translate-x-2"}`} />
                                        </button>
                                    </td>
                                ))}
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${u.is_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                        {u.is_active ? "active" : "inactive"}
                                    </span>
                                </td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex items-center gap-3 text-xs">
                                        <button onClick={() => resetPwd(u)} className="inline-flex items-center gap-1 text-zinc-500 hover:wl-accent-text" data-testid={`reset-pwd-${u.id}`}>
                                            <KeyRound className="h-3.5 w-3.5" />reset
                                        </button>
                                        {u.id !== user.id && u.role !== "super_admin" && (
                                            <button onClick={() => toggleActive(u)} className="text-zinc-500 hover:text-red-400" data-testid={`toggle-active-${u.id}`}>
                                                {u.is_active ? "Deactivate" : "Activate"}
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!users.length && (apiLoading ? <LoadingRow colSpan={7} /> : <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No users in scope.</td></tr>)}
                    </tbody>
                </table>
                {users.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="users-pagination"
                    />
                )}
            </div>

            {createOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCreateOpen(false)}>
                    <form onSubmit={submitCreate} onClick={(e) => e.stopPropagation()} className="bg-zinc-50 border border-zinc-200 w-full max-w-lg p-6 rounded-md" data-testid="user-create-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">New user</h3>
                            <button type="button" onClick={() => setCreateOpen(false)}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="overline">Role</label>
                                <select value={createForm.role} onChange={(e) => {
                                    const newRole = e.target.value;
                                    setCreateForm({
                                        ...createForm,
                                        role: newRole,
                                        // Management users see margin/spend rollups by default — toggle the flag on.
                                        can_view_actual_cost: newRole === "management" ? true : createForm.can_view_actual_cost,
                                    });
                                }} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="user-role-select">
                                    {user.role === "super_admin" && <option value="super_admin">Super Admin</option>}
                                    <option value="admin">Admin</option>
                                    <option value="management">Management</option>
                                    <option value="advertiser">Advertiser</option>
                                </select>
                            </div>
                            {user.role === "super_admin" && createForm.role !== "super_admin" && (
                                <div>
                                    <label className="overline">Agency</label>
                                    <select value={createForm.agency_id} onChange={(e) => setCreateForm({ ...createForm, agency_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                        <option value="">— pick —</option>
                                        {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                    </select>
                                </div>
                            )}
                            {createForm.role === "advertiser" && (
                                <div className="col-span-2">
                                    <label className="overline">Advertiser</label>
                                    <select required value={createForm.advertiser_id} onChange={(e) => setCreateForm({ ...createForm, advertiser_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                        <option value="">— pick advertiser —</option>
                                        {advertisers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                    </select>
                                </div>
                            )}
                            {createForm.role === "management" && (
                                <div className="col-span-2">
                                    <label className="overline">Allowed regions <span className="text-zinc-400 lowercase normal-case">(countries this user can see in their dashboard)</span></label>
                                    <MultiRegionPicker
                                        value={createForm.allowed_regions}
                                        onChange={(arr) => setCreateForm({ ...createForm, allowed_regions: arr })}
                                        testId="user-allowed-regions"
                                    />
                                </div>
                            )}
                            <div className="col-span-2">
                                <label className="overline">Full name</label>
                                <input required value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="user-fullname-input" />
                            </div>
                            <div>
                                <label className="overline">Email</label>
                                <input required type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="user-email-input" />
                            </div>
                            <div>
                                <label className="overline">Password</label>
                                <input required type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="user-password-input" />
                            </div>
                            {createForm.role === "admin" && (
                                <div className="col-span-2 grid grid-cols-3 gap-3 bg-zinc-50 border border-zinc-200 rounded p-3">
                                    {[
                                        { k: "can_view_actual_cost", label: "Campaign Spends" },
                                        { k: "can_manage_branding", label: "Branding" },
                                        { k: "can_connect_integrations", label: "Integrations" },
                                    ].map((p) => (
                                        <label key={p.k} className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                                            <input type="checkbox" checked={createForm[p.k]} onChange={(e) => setCreateForm({ ...createForm, [p.k]: e.target.checked })} />
                                            {p.label}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setCreateOpen(false)} className="px-3 py-2 text-sm text-zinc-500">Cancel</button>
                            <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="user-submit-btn">
                                <ShieldCheck className="inline h-4 w-4 mr-1" />Create user
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
